import { memo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import Container from '@ui/container/container';
import CastAndCrewHeader from './components/cast-and-crew-header';
import EmptyCrew from './components/empty-crew';
import EmptyCast from './components/empty-cast';
import CastCrewSectionHeader from './components/cast-crew-section-header';
import CastContent from './components/cast-content/cast-content';
import CrewContent from './components/crew-content/crew-content';
import useLoadSpeakers from '@/connection/hooks/use-load-speakers';
import CastHeaderActions from './components/cast-header-actions';
import CrewHeaderActions from './components/crew-header-actions';
import useFetchAdmins from '@/micros/homepage/utils/use-fetch-admins';
import useLoadWorkingSessionMeetingData from '@/micros/shared-create-and-edit-components/grid-areas/main/project-overview/cast-and-crew/cast-and-crew-select-with-invites/components/invite-cast-modal/use-get-working-session-meeting-data';
import useParamsOrFirstSessionUuid from '@/utils/use-params-or-first-session-uuid';
import Text from '@ui/text/text/text';
import AddCastModal from './components/add-cast-modal/add-cast-modal';
import AddCastAndCrewProvider from './cast-and-crew-context/cast-and-crew-provider';
import { useAppSelector } from '@/store/hooks';
import { selectUserChannel } from '@/store/slices/user/selectors';
import { isWebinarChannel } from '@/utils/project-types/project-type-guards';

const CastAndCrew = memo(() => {
  const userChannel = useAppSelector(selectUserChannel);
  useLoadSpeakers();
  useFetchAdmins({ includeLockedEmails: true, bypassIfExists: true });

  const sessionUuid = useParamsOrFirstSessionUuid();
  const channelIsWebinar = isWebinarChannel(userChannel?.type);

  useLoadWorkingSessionMeetingData({ sessionUuid });

  return (
    <AddCastAndCrewProvider>
      <Container className="flex-column gap-24" data-component="CastAndCrew">
        <Container padding="0 var(--main-content-horizontal-padding)">
          <CastAndCrewHeader />
        </Container>

        <ErrorBoundary
          fallback={
            <Text className="S1 radius-4 padding-h-8" design="LocalBttnCritical">
              Error loading cast
            </Text>
          }
        >
          <Container className="flex-column gap-8" padding="0 var(--main-content-horizontal-padding)">
            <CastCrewSectionHeader
              title="Cast"
              tooltipContent={`Speakers or presenters featured in this ${channelIsWebinar ? 'webinar' : 'townhall'}`}
            >
              <CastHeaderActions />
            </CastCrewSectionHeader>
            <EmptyCast />
            <CastContent />
          </Container>
        </ErrorBoundary>

        <ErrorBoundary
          fallback={
            <Text className="S1 radius-4 padding-h-8" design="LocalBttnCritical">
              Error loading crew
            </Text>
          }
        >
          <Container className="flex-column gap-8" padding="0 var(--main-content-horizontal-padding)">
            <CastCrewSectionHeader
              title="Crew"
              tooltipContent={`List of this ${channelIsWebinar ? 'webinar' : 'townhall'}'s producers`}
            >
              <CrewHeaderActions />
            </CastCrewSectionHeader>
            <EmptyCrew />
            <CrewContent />
          </Container>
        </ErrorBoundary>

        {/* single add cast modal so that it doesn't unmount when views change between empty and non-empty selected options */}
        <AddCastModal sessionUuid={sessionUuid} />
      </Container>
    </AddCastAndCrewProvider>
  );
});

CastAndCrew.displayName = 'CastAndCrew';

export default CastAndCrew;
